import { convertFromRaw, ContentState, convertToRaw } from "draft-js";
import stateToHTML from "./draftToHTML";

import {
  DRAFT_ENTITY,
  ALLOWED_ENTITY_DRAFT_TO_HTML,
} from "../../constants/document";
import { DRAFT } from "../../constants";

const generateDynamicColorOptions = contentState => {
  const rawContentState =
    contentState instanceof ContentState
      ? convertToRaw(contentState)
      : contentState;
  const { blocks } = rawContentState;

  if (blocks && blocks.length > 0) {
    const dynamicOptions = {};
    blocks.forEach(block => {
      if (block.inlineStyleRanges && block.inlineStyleRanges.length > 0) {
        block.inlineStyleRanges.forEach(inlineStyle => {
          if (inlineStyle.style.includes("FONTCOLOR_")) {
            dynamicOptions[`FONTCOLOR_${inlineStyle.style.split("_")[1]}`] = {
              style: { color: `#${inlineStyle.style.split("_")[1]}` },
            };
          }
          if (inlineStyle.style.includes("FONTTEXTHIGHLIGHT_")) {
            dynamicOptions[
              `FONTTEXTHIGHLIGHT_${inlineStyle.style.split("_")[1]}`
            ] = {
              style: { backgroundColor: `#${inlineStyle.style.split("_")[1]}` },
            };
          }
        });
      }
    });
    console.debug(dynamicOptions);
    return dynamicOptions;
  }
  return {};
};

const getEditorHTML = (contentState, mode = "PREVIEW") => {
  const dynamicInlineStyleOptions = generateDynamicColorOptions(contentState);

  const options = {
    defaultBlockTag: "p",
    inlineStyles: {
      ...dynamicInlineStyleOptions,
      // Font sizes options
      FONTSIZE_EIGHT: {
        style: { fontSize: "8px" },
      },
      FONTSIZE_NINE: {
        style: { fontSize: "9px" },
      },
      FONTSIZE_TEN: {
        style: { fontSize: "10px" },
      },
      FONTSIZE_ELEVEN: {
        style: { fontSize: "11px" },
      },
      FONTSIZE_TWELVE: {
        style: { fontSize: "12px" },
      },
      FONTSIZE_FOURTEEN: {
        style: { fontSize: "14px" },
      },
      FONTSIZE_SIXTEEN: {
        style: { fontSize: "16px" },
      },
      FONTSIZE_EIGHTEEN: {
        style: { fontSize: "18px" },
      },
      FONTSIZE_TWENTYFOUR: {
        style: { fontSize: "24px" },
      },
      FONTSIZE_THIRTY: {
        style: { fontSize: "30px" },
      },
      FONTSIZE_THIRTYSIX: {
        style: { fontSize: "36px" },
      },
      FONTSIZE_FOURTYEIGHT: {
        style: { fontSize: "48px" },
      },
      FONTSIZE_SIXTY: {
        style: { fontSize: "60px" },
      },
      FONTSIZE_SEVENTYTWO: {
        style: { fontSize: "72px" },
      },
      FONTSIZE_NINTYSIX: {
        style: { fontSize: "96px" },
      },
      FONTSIZE_ONEHUNDREDTWENTY: {
        style: { fontSize: "120px" },
      },
      FONTSIZE_ONEHUNDREDFOURTYFOUR: {
        style: { fontSize: "144px" },
      },
      // Font color options
      FONTCOLOR_000000: {
        style: { color: "#000000" },
      },
      FONTCOLOR_333333: {
        style: { color: "#333333" },
      },
      FONTCOLOR_888888: {
        style: { color: "#888888" },
      },
      FONTCOLOR_3333ff: {
        style: { color: "#3333ff" },
      },
      FONTCOLOR_66ccff: {
        style: { color: "#66ccff" },
      },
      FONTCOLOR_ff001c: {
        style: { color: "#ff001c" },
      },

      FONTCOLOR_9cd326: {
        style: { color: "#9cd326" },
      },
      FONTCOLOR_427d50: {
        style: { color: "#427d50" },
      },
      FONTCOLOR_72c9c5: {
        style: { color: "#72c9c5" },
      },
      FONTCOLOR_9dacbb: {
        style: { color: "#9dacbb" },
      },
      FONTCOLOR_9795ff: {
        style: { color: "#9795ff" },
      },
      FONTCOLOR_7053c9: {
        style: { color: "#7053c9" },
      },
      FONTCOLOR_ff0078: {
        style: { color: "#ff0078" },
      },
      FONTCOLOR_795449: {
        style: { color: "#795449" },
      },
      FONTCOLOR_e26d3f: {
        style: { color: "#e26d3f" },
      },
      FONTCOLOR_f1cb45: {
        style: { color: "#f1cb45" },
      },
      FONTCOLOR_bea938: {
        style: { color: "#bea938" },
      },
      FONTCOLOR_99218d: {
        style: { color: "#99218d" },
      },
      FONTCOLOR_ffffff: {
        style: { color: "#ffffff" },
      },
      FONTCOLOR_faff00: {
        style: { color: "#faff00" },
      },
      FONTCOLOR_740e0e: {
        style: { color: "#740e0e" },
      },
      FONTCOLOR_8c39f0: {
        style: { color: "#8c39f0" },
      },
      FONTCOLOR_0a269a: {
        style: { color: "#0a269a" },
      },
      FONTCOLOR_1f3e57: {
        style: { color: "#1f3e57" },
      },
      FONTCOLOR_def8f6: {
        style: { color: "#def8f6" },
      },
      FONTCOLOR_cfd4d9: {
        style: { color: "#cfd4d9" },
      },
      FONTCOLOR_c12928: {
        style: { color: "#c12928" },
      },
      // Font font family options
      FONTFAMILY_ARIAL: {
        style: { fontFamily: "Arial" },
      },
      FONTFAMILY_TIMESNEWROMAN: {
        style: { fontFamily: "Times New Roman" },
      },
      FONTFAMILY_TITILLIUMWEB: {
        style: { fontFamily: "Titillium Web" },
      },
      FONTFAMILY_HELVETICA: {
        style: { fontFamily: "Helvetica" },
      },
      FONTFAMILY_COURIERNEW: {
        style: { fontFamily: "Courier New" },
      },
      FONTFAMILY_ROBOTO: {
        style: { fontFamily: "Roboto" },
      },
      FONTFAMILY_ROBOTOCONDENSED: {
        style: { fontFamily: "Roboto Condensed" },
      },
      FONTFAMILY_GEORGIA: {
        style: { fontFamily: "Georgia" },
      },
      FONTFAMILY_BASKERVILLE: {
        style: { fontFamily: "Baskerville" },
      },
      FONTFAMILY_EBGARAMOND: {
        style: { fontFamily: "EB Garamond" },
      },
      FONTFAMILY_OSWALD: {
        style: { fontFamily: "Oswald" },
      },
      FONTFAMILY_OPENSANS: {
        style: { fontFamily: "Open Sans" },
      },
      FONTFAMILY_MONTSERRAT: {
        style: { fontFamily: "Montserrat" },
      },
      FONTFAMILY_LATO: {
        style: { fontFamily: "Lato" },
      },
      FONTFAMILY_INTER: {
        style: { fontFamily: "Inter" },
      },
      FONTFAMILY_PLAYFAIRDISPLAY: {
        style: { fontFamily: "Playfair Display" },
      },
      FONTFAMILY_POPPINS: {
        style: { fontFamily: "Poppins" },
      },
      FONTFAMILY_KALAM: {
        style: { fontFamily: "Kalam" },
      },
      FONTFAMILY_RALEWAY: {
        style: { fontFamily: "Raleway" },
      },
      FONTFAMILY_WORKSANS: {
        style: { fontFamily: "Work Sans" },
      },
      // Font weight options
      FONTWEIGHT_LIGHT: {
        style: { fontWeight: "300" },
      },
      FONTWEIGHT_REGULAR: {
        style: { fontWeight: "400" },
      },
      FONTWEIGHT_MEDIUM: {
        style: { fontWeight: "500" },
      },
      FONTWEIGHT_SEMIBOLD: {
        style: { fontWeight: "600" },
      },
      FONTWEIGHT_BOLD: {
        style: { fontWeight: "700" },
      },
      FONTWEIGHT_EXTRABOLD: {
        style: { fontWeight: "900" },
      },
    },
    entityStyleFn: entity => {
      const entityType = entity.get("type");
      const allowedEntity = ALLOWED_ENTITY_DRAFT_TO_HTML[mode] || [];
      const data = entity.getData();
      if (
        allowedEntity.findIndex(
          tempEntity => tempEntity === DRAFT_ENTITY.INSERTED,
        ) > -1 &&
        entityType === DRAFT_ENTITY.INSERTED
      ) {
        return {
          element: "span",
          attributes: {
            class: "added-document",
            id: data.id,
          },
          style: {
            // Put styles here...
          },
        };
      }
      if (
        allowedEntity.findIndex(
          tempEntity => tempEntity === DRAFT_ENTITY.MENTION,
        ) > -1 &&
        entityType === DRAFT_ENTITY.MENTION
      ) {
        return {
          element: "span",
          attributes: {
            class: "mention-user",
            id: data.id,
          },
          style: {
            // Put styles here...
          },
        };
      }
      if (
        allowedEntity.findIndex(
          tempEntity => tempEntity === DRAFT_ENTITY.DELETED,
        ) > -1 &&
        entityType === DRAFT_ENTITY.DELETED
      ) {
        return {
          element: "span",
          attributes: {
            class: "deleted-document",
            id: data.id,
          },
          style: {
            // Put styles here...
          },
        };
      }
      if (
        allowedEntity.findIndex(
          tempEntity => tempEntity === DRAFT_ENTITY.NOTES,
        ) > -1 &&
        entityType === DRAFT_ENTITY.NOTES
      ) {
        return {
          element: "span",
          attributes: {
            class: "document-highlight",
            id: data.id,
          },
          style: {
            // Put styles here...
          },
        };
      }
      if (
        allowedEntity.findIndex(
          tempEntity => tempEntity === DRAFT_ENTITY.COMMENTS,
        ) > -1 &&
        entityType === DRAFT_ENTITY.COMMENTS
      ) {
        return {
          element: "span",
          attributes: {
            class: "document-comments",
            id: data.id,
          },
          style: {
            // Put styles here...
          },
        };
      }
      if (
        allowedEntity.findIndex(
          tempEntity => tempEntity === DRAFT_ENTITY.DYNAMICFIELD,
        ) > -1 &&
        entityType === DRAFT_ENTITY.DYNAMICFIELD
      ) {
        return {
          element: "span",
          attributes: {
            class: data.value ? "dynamic-field-valid" : "dynamic-field",
            id: data.id,
            value: data.value,
          },
          style: {
            // Put styles here...
          },
        };
      }
      if (entityType === "HIGHLIGHT") {
        return {
          element: "span",
          attributes: {
            class: "document-selection-highlights",
            id: data.id,
          },
          style: {
            // Put styles here...
          },
        };
      }
      if (
        allowedEntity.findIndex(
          tempEntity => tempEntity === DRAFT_ENTITY.LINK,
        ) > -1 &&
        entityType === "LINK"
      ) {
        return {
          element: "a",
          attributes: {
            class: "document-links",
            id: data.id,
            href: data.url,
            target: "_blank",
          },
          style: {
            // Put styles here...
          },
        };
      }
      return null;
    },
  };
  let html;
  if (contentState instanceof ContentState) {
    html = stateToHTML(contentState, options);
  } else if (
    typeof contentState === "object" &&
    DRAFT.DRAFT_RAW_KEYS.length === Object.keys(contentState).length &&
    JSON.stringify(DRAFT.DRAFT_RAW_KEYS.sort()) ===
      JSON.stringify(Object.keys(contentState).sort())
  ) {
    html = stateToHTML(convertFromRaw(contentState), options);
  }
  return html;
};
export default getEditorHTML;
